import 'styles/scaffolding.scss';
import 'styles/typography.scss';
import 'styles/themes.scss';

import React, {ComponentType} from 'react';
import {I18N} from '@yandex-data-ui/i18n';
import _ from 'lodash';

import {
    getFullAnchorUrl,
    isLeftMouseButtonClick,
    isLocalLink,
    isModifiedEvent,
    scrollToHash,
    isElementInViewport,
} from 'utils';
import {loadGetInitialProps} from '../common/utils';
import configureStore from 'store/configureStore';

import Layout from 'components/Layout/Layout';
import LoaderBar from 'components/LoaderBar/LoaderBar';
import Header from 'units/navigation/components/Header/Header';
import BannerEvent from 'components/BannerEvent/BannerEvent';
import Footer from 'units/navigation/components/Footer/Footer';
import RegionTooltip from 'units/navigation/components/RegionTooltip/RegionTooltip';
import ContextProvider from 'components/ContextProvider/ContextProvider';
import {AppContext, AppData, InitialData, InitialProps, DocUserSettings} from 'models/app';
import {Provider} from 'react-redux';
import {DEFAULT_USER_SETTINGS} from '../common/constants';
import {setSettingToCookie, strToBoolean} from 'utils/docs';
import {TextSizes, Theme} from '@doc-tools/components';
import {HEADER_HEIGHT} from './constants';
import {WithEnvProps} from 'hoc/withEnv';
import {rumSendFrameworkInitialized} from 'services/rum';
import {ModalForm} from 'units/constructor-local/components';
import ErrorBoundary from 'components/ErrorBoundary';
import {I18nFunction, I18nKeysets, initI18n, loadKeysets} from 'i18n';

const store = configureStore();

export interface AppProps<TData extends Object> extends AppData, WithEnvProps {
    Component: ComponentType<TData>;
    pageProps: TData;
    context: AppContext;
    initialData: InitialData;
    docUserSettings: DocUserSettings;
    keysets: I18nKeysets;
}

const changeTheme = (theme: string) => {
    document.body.classList.toggle('yc-root_theme_light', theme === Theme.Light);
    document.body.classList.toggle('yc-root_theme_dark', theme === Theme.Dark);
};

export default class App<TData> extends React.Component<AppProps<TData>, DocUserSettings> {
    static async getInitialProps({Component, ctx}: InitialProps) {
        const [pageProps, keysets] = await Promise.all([
            loadGetInitialProps(Component, ctx),
            loadKeysets(ctx.locale.code),
        ]);

        return {
            pageProps,
            keysets,
            ...ctx.appData,
        };
    }

    i18n: I18nFunction;

    constructor(props: AppProps<TData>) {
        super(props);
        const {docUserSettings, keysets} = props;

        this.i18n = initI18n(keysets);
        I18N.setDefaultLang(props.context.locale.lang);

        this.state = this.prepareDocUserSettings(docUserSettings);
    }

    componentDidMount() {
        const {
            context: {router, browser},
        } = this.props;
        this.applyTheme();
        scrollToHash(router.hash, browser?.name);

        document.addEventListener('click', this.handleGlobalClick);
        router.events.on('routeChangeComplete', this.applyTheme);

        rumSendFrameworkInitialized();
    }

    componentDidUpdate(prevProps: Readonly<AppProps<TData>>) {
        const {
            context: {router, browser},
            isDocs,
            docUserSettings,
        } = this.props;
        scrollToHash(router.hash, browser?.name);

        if (prevProps.isDocs !== isDocs) {
            this.setState(this.prepareDocUserSettings(docUserSettings));
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleGlobalClick);
        this.props.context.router.events.off('routeChangeComplete', this.applyTheme);
    }

    render() {
        const {context, clearPage, pageProps} = this.props;

        return (
            <Provider store={store}>
                <ContextProvider context={context} i18n={this.i18n}>
                    {clearPage ? this.renderClearPage() : this.renderWithBasicLayout()}
                    {!__IS_SERVER__ && <ModalForm service={_.get(pageProps, 'data.service')} />}
                </ContextProvider>
            </Provider>
        );
    }

    private prepareDocUserSettings = (settings = DEFAULT_USER_SETTINGS) => {
        return {
            fullScreen: strToBoolean(settings.fullScreen),
            wideFormat: strToBoolean(settings.wideFormat),
            showMiniToc: strToBoolean(settings.showMiniToc),
            theme: settings.theme as Theme,
            textSize: settings.textSize as TextSizes,
        };
    };

    private applyTheme = () => {
        const {theme} = this.state;
        const {isDocs} = this.props;

        changeTheme(isDocs ? theme : Theme.Light);
    };

    private renderClearPage() {
        return this.renderPage();
    }

    private renderWithBasicLayout() {
        const {noHeader, isDocs} = this.props;
        const {fullScreen} = this.state;

        return (
            <React.Fragment>
                <Layout noHeader={noHeader} noFooter={isDocs} type={isDocs ? 'doc' : undefined}>
                    <Layout.Header>
                        <BannerEvent />
                        {fullScreen ? null : <Header />}
                        <LoaderBar />
                    </Layout.Header>

                    <Layout.Content>
                        <RegionTooltip />
                        {this.renderPage()}
                    </Layout.Content>

                    <Layout.Footer>
                        <Footer />
                    </Layout.Footer>
                </Layout>
            </React.Fragment>
        );
    }

    private getUserSettings = () => {
        const {wideFormat, fullScreen, showMiniToc, theme, textSize} = this.state;

        return {
            wideFormat,
            fullScreen,
            showMiniToc,
            theme,
            textSize,
            onChangeFullScreen: this.makeOnChangeUserSetting('fullScreen', {setToCookies: false}),
            onChangeWideFormat: this.makeOnChangeUserSetting('wideFormat'),
            onChangeShowMiniToc: this.makeOnChangeUserSetting('showMiniToc'),
            onChangeTheme: this.makeOnChangeUserSetting('theme', {
                onChange: (newTheme) => changeTheme(newTheme as Theme),
            }),
            onChangeTextSize: this.makeOnChangeUserSetting('textSize'),
            headerHeight: HEADER_HEIGHT,
        };
    };

    private makeOnChangeUserSetting =
        (
            settingName: keyof DocUserSettings,
            {
                onChange,
                setToCookies = true,
            }: {onChange?: (value: unknown) => void; setToCookies?: boolean} = {},
        ) =>
        (value: unknown) => {
            if (setToCookies) {
                setSettingToCookie(settingName, value);
            }

            this.setState(
                {[settingName]: value} as Pick<DocUserSettings, keyof DocUserSettings>,
                () => {
                    if (typeof onChange === 'function') {
                        onChange(value);
                    }
                },
            );
        };

    private renderPage() {
        const {Component, pageProps} = this.props;
        const userSettings = this.getUserSettings();
        const props = {
            ...pageProps,
            ...userSettings,
        };

        return (
            <ErrorBoundary>
                <Component {...props} />
            </ErrorBoundary>
        );
    }

    private handleGlobalClick = async (event: MouseEvent) => {
        const {
            context: {router},
        } = this.props;

        const targetElement = event?.target as Element;
        const anchorEl =
            typeof targetElement?.closest === 'function' ? targetElement?.closest('a') : null;
        const enabled = Boolean(anchorEl && anchorEl.dataset.router !== 'off');
        const isTargetBlank = Boolean(anchorEl && anchorEl.target === '_blank');

        if (
            anchorEl &&
            enabled &&
            !isModifiedEvent(event) &&
            isLeftMouseButtonClick(event) &&
            isLocalLink(anchorEl.href) &&
            !isTargetBlank
        ) {
            const replace = typeof anchorEl.dataset.routerReplace === 'string';
            const shallow = typeof anchorEl.dataset.routerShallow === 'string';
            const scroll = typeof anchorEl.dataset.routerNoScroll === 'undefined';

            const method = replace ? 'replace' : 'push';
            const href = anchorEl.getAttribute('href');
            const url = getFullAnchorUrl(href);

            event.preventDefault();

            const result = await router[method](url, url, {shallow});

            if (!result) {
                return;
            }

            if (scroll) {
                const hashElement = document.getElementById(router.hash);
                const elStyle = hashElement ? window.getComputedStyle(hashElement) : null;

                if (!elStyle || elStyle.display === 'none' || elStyle.visibility === 'hidden') {
                    window.scrollTo(0, 0);
                    if (!document.activeElement || !isElementInViewport(document.activeElement)) {
                        document.body.focus();
                    }
                }
            }
        }
    };
}
